import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { t } from 'i18next';
import moment from 'moment';
import { Card, List, Divider, Typography, CardHeader, CardContent } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useRouter } from '@/routes/hooks';
import {
  showMilestoneInUseSnackbar,
  showMultipleMilestonesInUseSnackbar,
} from '@/utils/billing/billing-milestones-snackbar';
import {
  IMonthlyData,
  IBillingFilters,
  IMilestoneSelected,
  MILESTONES_PER_PAGE,
  sortMonthlyDataByDate,
  DEFAULT_BILLING_FILTERS,
} from '@/components/data-display/billing-milestones/types';
import { OrderTypes } from '@/types';
import {
  FiltersBar,
  MonthAccordion,
  SelectedMilestonesPanel,
} from '@/components/data-display/billing-milestones/components';
import { useBillingMilestones } from '@/hooks/data/use-billing-milestones';

interface Props {
  organizationId: string;
  value?: string[];
  currentOrderId?: string;
  onMilestonesChange?: (data: {
    milestones: string[];
    orderType: string;
    selectedMilestones: IMilestoneSelected[];
    monthlyData?: IMonthlyData[];
  }) => void;
  inUseMilestones?: string[];
}

const generateOptionsFromMonthlyData = (monthlyData: IMonthlyData[]) => {
  if (!monthlyData?.length) return { yearOptions: [], monthOptions: [] };

  const dates = monthlyData.map((d) => moment(d._id, 'YYYY-MM')).filter((d) => d.isValid());
  const years = [...new Set(dates.map((d) => d.year()))].sort((a, b) => a - b);
  const months = [...new Set(dates.map((d) => d.month() + 1))].sort((a, b) => a - b);

  return {
    yearOptions: years,
    monthOptions: months.map((m) => ({
      value: m,
      label: moment()
        .month(m - 1)
        .format('MMMM'),
    })),
  };
};

export default function BillingMilestonesSelector({
  organizationId,
  value,
  currentOrderId,
  onMilestonesChange,
  inUseMilestones = [],
}: Props) {
  const router = useRouter();
  const [selectedMilestones, setSelectedMilestones] = useState<IMilestoneSelected[]>([]);
  const [filters, setFilters] = useState<IBillingFilters>(DEFAULT_BILLING_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedSubscriptions, setExpandedSubscriptions] = useState<Set<string>>(new Set());

  const {
    data: monthlyData = [],
    isLoading: loading,
    error,
  } = useBillingMilestones({
    organizationId,
    filters,
  });

  const isMilestoneAvailable = useMemo(
    () =>
      (milestone: IMilestoneSelected): boolean => {
        if (!milestone?.billingOrders) return true;
        const { billingOrders } = milestone;
        return (
          billingOrders.length === 0 ||
          Boolean(
            currentOrderId && billingOrders.some((order: any) => order._id === currentOrderId)
          )
        );
      },
    [currentOrderId]
  );

  const { yearOptions, monthOptions } = useMemo(
    () => generateOptionsFromMonthlyData(monthlyData),
    [monthlyData]
  );

  const handleMilestonesChange = useCallback(
    (updatedMilestones: IMilestoneSelected[]) => {
      const filtered = updatedMilestones.filter((m) => !inUseMilestones.includes(m._id));
      setSelectedMilestones(filtered);
      onMilestonesChange?.({
        milestones: filtered.map((m) => m._id),
        orderType: OrderTypes.BILLING_ORDER,
        selectedMilestones: filtered,
        monthlyData,
      });
    },
    [onMilestonesChange, monthlyData, inUseMilestones]
  );

  useEffect(() => {
    if (!value?.length || !monthlyData.length) return;

    const allMilestones = monthlyData.flatMap((m: IMonthlyData) => m.milestones);
    const initialSelected = allMilestones.filter((m: IMilestoneSelected) => value.includes(m._id));
    setSelectedMilestones(initialSelected);
  }, [value, monthlyData]);

  const toggleMilestone = useCallback(
    (milestone: IMilestoneSelected) => {
      const isSelected = selectedMilestones.some((m) => m._id === milestone._id);

      if (isSelected) {
        const updated = selectedMilestones.filter((m) => m._id !== milestone._id);
        handleMilestonesChange(updated);
      } else {
        if (!isMilestoneAvailable(milestone)) {
          showMilestoneInUseSnackbar(milestone, router, organizationId);
          return;
        }
        handleMilestonesChange([...selectedMilestones, milestone]);
      }
    },
    [selectedMilestones, handleMilestonesChange, router, organizationId, isMilestoneAvailable]
  );

  const toggleMonthMilestones = useCallback(
    (monthMilestones: IMilestoneSelected[]) => {
      if (!monthMilestones?.length) return;

      const availableMilestones = monthMilestones.filter(isMilestoneAvailable);
      const milestonesInUse = monthMilestones.filter((m) => !isMilestoneAvailable(m));

      if (milestonesInUse.length > 0) {
        showMultipleMilestonesInUseSnackbar();
      }

      if (availableMilestones.length === 0) return;

      const allSelected = availableMilestones.every((milestone) =>
        selectedMilestones.some((m) => m._id === milestone._id)
      );

      if (allSelected) {
        const updated = selectedMilestones.filter(
          (selected) => !availableMilestones.some((available) => available._id === selected._id)
        );
        handleMilestonesChange(updated);
      } else {
        const toAdd = availableMilestones.filter(
          (milestone) => !selectedMilestones.some((m) => m._id === milestone._id)
        );
        handleMilestonesChange([...selectedMilestones, ...toAdd]);
      }
    },
    [selectedMilestones, handleMilestonesChange, isMilestoneAvailable]
  );

  const toggleSubscriptionExpanded = useCallback((subscriptionId: string) => {
    setExpandedSubscriptions((prev) => {
      const next = new Set(prev);
      if (next.has(subscriptionId)) {
        next.delete(subscriptionId);
      } else {
        next.add(subscriptionId);
      }
      return next;
    });
  }, []);

  const isMonthFullySelected = useCallback(
    (monthMilestones: IMilestoneSelected[]) => {
      if (!monthMilestones?.length) return false;
      const availableMilestones = monthMilestones.filter(isMilestoneAvailable);
      if (availableMilestones.length === 0) return false;
      return availableMilestones.every((milestone) =>
        selectedMilestones.some((m) => m._id === milestone._id)
      );
    },
    [selectedMilestones, isMilestoneAvailable]
  );

  const handleRemoveMilestone = useCallback(
    (milestoneId: string) => {
      const updatedMilestones = selectedMilestones.filter((m) => m._id !== milestoneId);
      handleMilestonesChange(updatedMilestones);

      const totalPages = Math.ceil(updatedMilestones.length / MILESTONES_PER_PAGE);
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(totalPages);
      }
    },
    [selectedMilestones, handleMilestonesChange, currentPage]
  );

  const handleClearSelection = useCallback(() => {
    handleMilestonesChange([]);
    setCurrentPage(1);
  }, [handleMilestonesChange]);

  return (
    <Card>
      <CardHeader title={t('Select the milestone you want to bill')} />
      <CardContent>
        <FiltersBar
          filters={filters}
          setFilters={setFilters}
          yearOptions={yearOptions}
          monthOptions={monthOptions}
        />

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid xs={12} md={8}>
            {loading && (
              <Typography variant="body2" sx={{ p: 2 }}>
                {t('Loading...')}
              </Typography>
            )}

            {!!error && (
              <Typography color="error" variant="body2" sx={{ p: 2 }}>
                {t('Failed to fetch billing data')}
              </Typography>
            )}

            {!loading && !error && (
              <List>
                {sortMonthlyDataByDate(monthlyData).map((monthData) => (
                  <MonthAccordion
                    key={monthData._id}
                    monthData={monthData}
                    selectedMilestones={selectedMilestones}
                    expandedSubscriptions={expandedSubscriptions}
                    isMilestoneAvailable={isMilestoneAvailable}
                    isMonthFullySelected={isMonthFullySelected}
                    onToggleMilestone={toggleMilestone}
                    onToggleMonthMilestones={toggleMonthMilestones}
                    onToggleSubscriptionExpanded={toggleSubscriptionExpanded}
                    inUseMilestones={inUseMilestones}
                  />
                ))}
              </List>
            )}
          </Grid>

          <Grid xs={12} md={4}>
            <SelectedMilestonesPanel
              selectedMilestones={selectedMilestones}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              onRemoveMilestone={handleRemoveMilestone}
              onClearSelection={handleClearSelection}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
